import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

import { arActionLabel } from "./arActions";

interface InsuranceArRecordCallDialogProps {
  entry: InsuranceArEntryMessage | null;
  callNextStepActions: string[];
  onClose: () => void;
  onConfirm: (nextAction: string) => Promise<void>;
}

export default function InsuranceArRecordCallDialog({
  entry,
  callNextStepActions,
  onClose,
  onConfirm,
}: InsuranceArRecordCallDialogProps) {
  const [nextAction, setNextAction] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping entry would change when this runs
  React.useEffect(() => {
    setNextAction("");
    setSaving(false);
  }, [entry]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!nextAction) return;
    setSaving(true);
    try {
      await onConfirm(nextAction);
    } finally {
      setSaving(false);
    }
  };

  const patientName =
    entry?.patientFullName ??
    [entry?.patientFirstName, entry?.patientLastName].filter(Boolean).join(" ");
  const payerName = entry?.payerName ?? entry?.insuranceNameRaw ?? "";

  return (
    <Dialog open={entry !== null} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Record call{patientName ? ` — ${patientName}` : ""}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginTop: "8px" }}>
          <Typography sx={{ fontSize: "13px", color: "#475467" }}>
            {payerName ? `${payerName} · ` : ""}Calling the payer is
            investigative. Log the call and pick the next step — the balance
            stays in Needs work until that step is recorded.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="insurance-ar-call-next-label">
              Next step after call
            </InputLabel>
            <Select
              labelId="insurance-ar-call-next-label"
              label="Next step after call"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
            >
              {callNextStepActions.map((option) => (
                <MenuItem key={option} value={option}>
                  {arActionLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || !nextAction}
        >
          {saving ? "Saving…" : "Record call"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
